import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

export const EXCLUSION_CHECK_TITLE = "Exclusion & Sanction Check";
export const EXCLUSION_CHECK_SUBTITLE =
  "Screen providers, vendors, and employees against federal and state exclusion lists.";

export type ExclusionCheckSurfaceTab = "new-check" | "history";

export type ExclusionCheckShellProps = {
  /** Content shown on the New check tab (default). */
  newCheckContent: React.ReactNode;
  /** Content shown on the History tab. */
  historyContent: React.ReactNode;
  /** Initial tab; defaults to New check. */
  defaultTab?: ExclusionCheckSurfaceTab;
  /** Optional controlled tab (tests / deep-links). */
  activeTab?: ExclusionCheckSurfaceTab;
  /** Called when the user selects a tab. */
  onTabChange?: (tab: ExclusionCheckSurfaceTab) => void;
};

/**
 * Page shell for My Facility → Exclusion & Sanction Check (#10113):
 * title, subtitle, and New check | History tabs.
 */
export default function ExclusionCheckShell({
  newCheckContent,
  historyContent,
  defaultTab = "new-check",
  activeTab: controlledTab,
  onTabChange,
}: ExclusionCheckShellProps) {
  const [uncontrolledTab, setUncontrolledTab] =
    useState<ExclusionCheckSurfaceTab>(defaultTab);
  const activeTab = controlledTab ?? uncontrolledTab;

  const selectTab = (tab: ExclusionCheckSurfaceTab) => {
    if (controlledTab === undefined) {
      setUncontrolledTab(tab);
    }
    onTabChange?.(tab);
  };

  return (
    <Box
      data-testid="exclusion-check-shell"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      <Box
        sx={{
          px: { xs: 0, sm: 0 },
          pt: 0,
          pb: 0,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Typography
          component="h1"
          data-testid="exclusion-check-title"
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontWeight: 600,
            fontSize: "22px",
            color: "#155e75",
            letterSpacing: "-0.01em",
            mb: "4px",
          }}
        >
          {EXCLUSION_CHECK_TITLE}
        </Typography>
        <Typography
          data-testid="exclusion-check-subtitle"
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontWeight: 400,
            fontSize: "13px",
            color: "#64748b",
            mb: "18px",
            maxWidth: "820px",
          }}
        >
          {EXCLUSION_CHECK_SUBTITLE}
        </Typography>
        <Box
          role="tablist"
          aria-label="Exclusion check views"
          sx={{ display: "flex", gap: "26px" }}
        >
          <ShellTab
            id="exclusion-tab-new-check"
            selected={activeTab === "new-check"}
            onClick={() => selectTab("new-check")}
          >
            New check
          </ShellTab>
          <ShellTab
            id="exclusion-tab-history"
            selected={activeTab === "history"}
            onClick={() => selectTab("history")}
          >
            History
          </ShellTab>
        </Box>
      </Box>

      {/* Keep both panels mounted so New check state survives History tab switches (AB#10332). */}
      <Box sx={{ pt: "32px", pb: "24px" }}>
        <Box
          role="tabpanel"
          aria-labelledby="exclusion-tab-new-check"
          hidden={activeTab !== "new-check"}
          aria-hidden={activeTab !== "new-check"}
          sx={{ display: activeTab === "new-check" ? "block" : "none" }}
        >
          {newCheckContent}
        </Box>
        <Box
          role="tabpanel"
          aria-labelledby="exclusion-tab-history"
          hidden={activeTab !== "history"}
          aria-hidden={activeTab !== "history"}
          sx={{ display: activeTab === "history" ? "block" : "none" }}
        >
          {historyContent}
        </Box>
      </Box>
    </Box>
  );
}

function ShellTab({
  id,
  selected,
  onClick,
  children,
}: {
  id?: string;
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      id={id}
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      sx={{
        all: "unset",
        cursor: "pointer",
        fontFamily: "Rethink Sans, sans-serif",
        fontSize: "14px",
        fontWeight: selected ? 600 : 400,
        color: selected ? "#155e75" : "#64748b",
        padding: "10px 0",
        marginBottom: "-1px",
        borderBottom: selected ? "2px solid #0e7490" : "2px solid transparent",
        "&:hover": {
          color: "#155e75",
        },
        "&:focus-visible": {
          outline: "2px solid #0e7490",
          outlineOffset: "2px",
        },
      }}
    >
      {children}
    </Box>
  );
}

/** Placeholder History panel until story #10121 ships the run list. */
export function ExclusionCheckHistoryPlaceholder() {
  return (
    <Box data-testid="exclusion-check-history-placeholder">
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 600,
          fontSize: "16px",
          color: "#0f172a",
          mb: "4px",
        }}
      >
        Run history
      </Typography>
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 400,
          fontSize: "13px",
          color: "#64748b",
        }}
      >
        Every past check. Download any run&apos;s audit copy.
      </Typography>
    </Box>
  );
}
